import { getViemClient, getViemClients, } from "@bracket/contractor-next";
import type { Config, } from "@wagmi/core";
import type { ValidChainId, } from "@types";
import type { USD, } from "@bracket/types";

import {
	getContractWithABI,
	getContractAddress,
	getTokenDefinition,
} from "@bracket/chainography";


/**
 * @param {"ETH_USD"|"BTC_USD"} token
 * @param {number?} chainId the chain id to use, defaults to 1
 * @param {Config?} config the wagmi config to use, required if called from a browser
 * @param {number?} age the allowed age of the result, defaults to 0 (no cache)
 */
export declare type getLatestPriceProps = {
	token?: "ETH_USD"|"BTC_USD";
	chain?: ValidChainId;
	chainId?: ValidChainId;
	config?: Config;
	age?: number;
}

const cache = new Map<string, { since: number, value: number }>();

/**
 * Get the latest price of ETH or BTC in USD
 * @param {getLatestPriceProps} props
 * @returns {Promise<USD>} the price of the token in USD
 */
export const getLatestPrice = async({
	token = "ETH_USD",
	chainId = 1,
	config,
	age = 0,
}: getLatestPriceProps = { }): Promise<number> => {
	try {
		if(age > 0) {
			const cached = cache.get(token);
			if(cached) {
				if(Date.now() - cached.since < age) {
					return cached.value;
				}
			}
		}
		const client = getViemClient({ chainId, config, })
		const [contract, abi] = getContractWithABI("Pricing", chainId);
		const token_address = getContractAddress(token);
		const result = await client.readContract({
			address: contract,
			abi,
			functionName: "getLatestPrice",
			args: [
				token_address
			],
		})
		if(typeof result === "bigint") {
			const value = Number(result) / 1e18;
			if(age > 0) {
				cache.set(token, { since: Date.now(), value, });
			}
			return value;
		}
		throw new Error("reached exception");
	}
	catch( err ) {
		throw new Error(`@bracket/contractor-next::getLatestPrice failed - ${err.message}`);
	}
}
